import { Head } from '@inertiajs/react'
import React, { useEffect, useState } from 'react'

interface SurveyLayoutProps {
  children: React.ReactNode
}

/**
 * Layout for Panorama survey pages. Replicates the visual style from the
 * static ERB page at /panorama-ecommerce-2026, but renders as a React
 * component so Inertia pages can use it via the `.layout` static property.
 *
 * Overrides the global `overflow: hidden` set in application.css so the
 * survey content can scroll normally.
 */
const SurveyLayout: React.FC<SurveyLayoutProps> = ({ children }) => {
  const [pirateFlag, setPirateFlag] = useState('/landing/images/pirate-flag.png')

  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const app = document.getElementById('app')

    html.style.overflow = 'auto'
    html.style.height = 'auto'
    body.style.overflow = 'auto'
    body.style.height = 'auto'
    if (app) {
      app.style.overflow = 'auto'
      app.style.height = 'auto'
    }

    return () => {
      html.style.overflow = ''
      html.style.height = ''
      body.style.overflow = ''
      body.style.height = ''
      if (app) {
        app.style.overflow = ''
        app.style.height = ''
      }
    }
  }, [])

  return (
    <>
      <Head>
        <title>Panorama E-commerce 2026 - StationZero</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://sz-images.s3.us-west-2.amazonaws.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Inter:wght@400;500;600;700&family=Pirata+One&display=swap"
          rel="stylesheet"
        />
      </Head>

      <div
        className="min-h-screen font-sans text-slate-800"
        style={{ backgroundColor: '#fafafa' }}
      >
        {/* Glass-morphism navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
          <div
            className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between rounded-xl"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.4) 100%)',
              backdropFilter: 'blur(24px) saturate(1.8)',
              WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
              border: '1px solid rgba(255,255,255,0.5)',
              boxShadow:
                '0 1px 2px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(255,255,255,0.2)',
            }}
          >
            <a href="https://stationzero.ai" className="flex items-center">
              <img
                src="/landing/images/sz-orb-logo.png"
                alt="StationZero"
                className="h-6 object-contain"
              />
            </a>

            <a
              href="https://stationzero.ai"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
            >
              Ir para o site
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M7 17L17 7" />
                <path d="M7 7h10v10" />
              </svg>
            </a>
          </div>
        </nav>

        {/* Main content */}
        <main className="pt-24 pb-16">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">{children}</div>
        </main>

        {/* Footer */}
        <footer
          className="pt-12 pb-24 border-t border-slate-100"
          style={{ backgroundColor: '#fafafa' }}
        >
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-16 px-5">
              {/* Left: Logo & Info */}
              <div className="flex flex-col gap-8">
                <div className="flex items-center">
                  <img
                    src="/landing/images/sz-orb-logo.png"
                    alt="StationZero"
                    className="h-8 object-contain"
                  />
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Alameda Vicente Pinzon, 54
                  <br />
                  Vila Olímpia, São Paulo
                  <br />
                  CEP: 04547-130
                  <br />
                  StationZero Tecnologias LTDA.
                  <br />
                  CNPJ: 60.352.389/0001-66
                </p>
                <div className="flex items-center gap-2.5">
                  <img
                    src={pirateFlag}
                    alt="Pirate flag"
                    className="w-[60px] h-[40px] object-contain transition-opacity duration-200"
                    style={{ imageRendering: 'pixelated' }}
                    onMouseEnter={() =>
                      setPirateFlag('/landing/images/pirate-flag-hover.png')
                    }
                    onMouseLeave={() =>
                      setPirateFlag('/landing/images/pirate-flag.png')
                    }
                  />
                  <p
                    className="text-slate-500 leading-[1.2em] tracking-[-0.02em]"
                    style={{ fontFamily: "'Pirata One', cursive", fontSize: '16px' }}
                  >
                    Blood, sweat, and pixel dust.
                    <br />
                    Made by a crew of misfits from Brazil.
                  </p>
                </div>
              </div>

              {/* Right side */}
              <div className="flex flex-col items-center md:items-end gap-10">
                {/* Legal Links */}
                <div className="flex flex-col items-start md:items-end w-full md:w-auto gap-5">
                  <h4 className="text-sm font-medium text-slate-900">Legal</h4>
                  <a
                    href="/privacy"
                    className="text-sm text-slate-400 hover:text-slate-900 transition-colors"
                  >
                    Privacidade
                  </a>
                  <a
                    href="/terms"
                    className="text-sm text-slate-400 hover:text-slate-900 transition-colors"
                  >
                    Termos
                  </a>
                </div>

                <p className="text-xs text-slate-300 font-light">
                  StationZero® todos direitos reservados
                </p>
              </div>
            </div>

            {/* Program Logos */}
            <div className="flex flex-col items-center pt-10 pb-4 border-t border-slate-100">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-6">Proudly supported by</p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-10 md:gap-14">
              <img src="/landing/images/antler-logo.png" alt="Antler" className="w-36 md:w-auto md:h-10 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-200" />
              <img src="/landing/images/aws-startups.png" alt="AWS Startup Programs" className="w-36 md:w-auto md:h-12 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-200" />
              <img src="/landing/images/google-for-startups.png" alt="Google for Startups" className="w-20 md:w-auto md:h-16 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-200" />
              <img src="/landing/images/nvidia-inception-logo.png" alt="NVIDIA Inception Program" className="w-32 md:w-auto md:h-20 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-200" />
              </div>
            </div>
          </div>
        </footer>

        {/* Bottom image */}
        <div className="overflow-hidden" style={{ backgroundColor: '#fafafa' }}>
          <img
            src="/landing/images/liquid-glass-footer.png"
            alt="StationZero"
            className="w-full object-cover"
          />
        </div>
      </div>
    </>
  )
}

export default SurveyLayout
